import { LoginRequest, LoginResponse, SessionSettings } from '../types/auth';
import { User } from '../types/user';
import apiService from './api';
import StorageService from './storage';
import websocketService from './websocket';
import tokenRefreshService from './tokenRefresh';
import { authInterceptor } from './authInterceptor';

interface PersistLoginOptions {
  user: User;
  sessionSettings?: SessionSettings | null;
  hospital?: any;
  accessibleHospitals?: any[] | null;
  token?: string;
}

export class AuthService {
  private static readonly DEFAULT_SESSION_SETTINGS: SessionSettings = {
    hospital_id: null,
    // Default to DISABLED until the backend confirms the hospital's policy.
    max_login_duration_enabled: false,
    max_login_duration_minutes: 480,
    max_login_warning_minutes: 5,
    idle_timeout_enabled: false,
    idle_timeout_minutes: 20,
    idle_warning_minutes: 3,
    session_check_interval_minutes: 5,
  };

  // Browser detection for Edge-specific handling
  private static isEdgeBrowser(): boolean {
    return /Edg/.test(navigator.userAgent);
  }

  // Wait for cookie to be set properly in Edge
  private static async waitForCookie(cookieName: string, maxWait: number = 1500): Promise<boolean> {
    const startTime = Date.now();
    while (Date.now() - startTime < maxWait) {
      if (document.cookie.includes(cookieName)) {
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    return false;
  }

  static persistLoginState({
    user,
    sessionSettings,
    hospital,
    accessibleHospitals,
    token = 'cookie',
  }: PersistLoginOptions): void {
    const resolvedSessionSettings =
      sessionSettings ??
      StorageService.getSessionSettings() ??
      AuthService.DEFAULT_SESSION_SETTINGS;

    const loginResponse: LoginResponse = {
      token,
      user_id: user.id,
      role: (user as any)?.role_display ?? (user as any)?.role ?? '',
      role_specialty: user.role_specialty?.name ?? null,
      hierarchy_level: (user as any)?.hierarchy_level ?? 0,
      department: (user as any)?.primary_department?.name ?? null,
      mfa_enabled: Boolean((user as any)?.mfa_enabled),
      session_settings: resolvedSessionSettings,
      hospital: hospital || undefined,
      accessible_hospitals: accessibleHospitals ?? undefined,
    };

    StorageService.setAuthData(loginResponse, user);
    StorageService.setSessionSettings(resolvedSessionSettings);
    authInterceptor.updateValidationInterval(
      resolvedSessionSettings.session_check_interval_minutes
    );

    const now = Date.now();
    StorageService.setSessionMetadata({
      loginTime: now,
      lastActivity: now,
      userAgent: navigator.userAgent,
    });
    StorageService.setLastValidation(now);

    if (hospital) {
      StorageService.setHospital(hospital);
    }
    if (accessibleHospitals) {
      StorageService.setAccessibleHospitals(accessibleHospitals);
    }

    tokenRefreshService.startAutoRefresh(true);
  }

  static async login(credentials: LoginRequest): Promise<LoginResponse & { user: User }> {
    const response = await apiService.post<LoginResponse>(
      'auth/login/',
      credentials
    );

    // With cookie-based auth, token is automatically set in httpOnly cookie by server
    // No need to store token in localStorage - it's handled by cookies
    
    // Edge browser fix: Wait for cookies to be properly set
    if (this.isEdgeBrowser()) {
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.log('🌐 Edge browser detected, waiting for cookies to be set...');
      }
      await this.waitForCookie('sessionid');
      await this.waitForCookie('csrftoken');
      // Reduced delay for Edge to process cookies
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    
    // Fetch user details (now authenticated via cookie)
    const userResponse = await apiService.get<User>(
      'users/me/'
    );

    // Store user data and auth info (but not token)
    const loginResponse: LoginResponse = {
      ...response, // Include all response fields from server
      token: 'cookie', // Placeholder since token is in httpOnly cookie
    };
    
    AuthService.persistLoginState({
      user: userResponse,
      sessionSettings: response.session_settings,
      hospital: response.hospital,
      accessibleHospitals: response.accessible_hospitals,
      token: loginResponse.token,
    });

    return { ...loginResponse, user: userResponse };
  }

  // ---- Multi-factor authentication (TOTP) ----
  /** Fetch the QR code + secret to enroll an authenticator app. */
  static async getMfaSetup(): Promise<{ qr_code: string; secret: string; enabled: boolean }> {
    return apiService.get('auth/mfa/setup/');
  }

  /** Confirm enrollment by verifying a code; returns one-time recovery codes. */
  static async enableMfa(token: string): Promise<{ message: string; backup_codes: string[] }> {
    return apiService.post('auth/mfa/enable/', { token });
  }

  /** Turn MFA off (requires the account password). */
  static async disableMfa(password: string): Promise<{ message: string }> {
    return apiService.post('auth/mfa/disable/', { password });
  }

  /** Regenerate recovery codes (invalidates the old set; requires password). */
  static async regenerateBackupCodes(password: string): Promise<{ message: string; backup_codes: string[] }> {
    return apiService.post('auth/mfa/backup-codes/regenerate/', { password });
  }

  /** Superuser: reset another user's MFA so they can re-enroll. */
  static async resetMfaForUser(userId: number): Promise<{ message: string }> {
    return apiService.post('auth/mfa/reset/', { user_id: userId });
  }

  static async logout(): Promise<void> {
    try {
      // Stop automatic token refresh
      tokenRefreshService.stopAutoRefresh();
      
      // Disconnect WebSocket first to prevent unnecessary reconnection attempts
      websocketService.disconnect();
      
      // Call backend logout endpoint to invalidate server-side token
      await apiService.post('auth/logout/');
    } catch (error) {
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.warn('Logout API call failed:', error);
      }
      // Continue with cleanup even if API call fails
    } finally {
      // Always clear local storage regardless of API success/failure
      StorageService.clearAuthData();
    }
  }

  static getToken(): string | null {
    return StorageService.getToken();
  }

  static getUser(): User | null {
    return StorageService.getUser();
  }

  static isAuthenticated(): boolean {
    const hasToken = !!StorageService.getToken();
    const hasUser = !!StorageService.getUser();
    const isSessionValid = StorageService.isSessionValid();
    
    return hasToken && hasUser && isSessionValid;
  }
  
  static updateActivity(): void {
    // Update last activity timestamp to maintain session
    StorageService.updateLastActivity();
  }
  
  static getAuthenticationState(): {
    isAuthenticated: boolean;
    needsValidation: boolean;
    isOffline: boolean;
    lastValidation: number | null;
  } {
    const token = StorageService.getToken();
    const user = StorageService.getUser();
    const lastValidation = StorageService.getLastValidation();
    const isOffline = StorageService.isOfflineMode();
    const shouldRevalidate = StorageService.shouldRevalidate();
    
    return {
      isAuthenticated: !!(token && user),
      needsValidation: shouldRevalidate && !isOffline,
      isOffline,
      lastValidation
    };
  }

  static hasRole(role: string): boolean {
    const user = StorageService.getUser();
    return user?.role_display === role || user?.role_specialty?.name === role;
  }

  static hasAnyRole(roles: string[]): boolean {
    const user = StorageService.getUser();
    if (!user) return false;
    
    return roles.some(role => 
      user.role_display === role || 
      user.role_specialty?.name === role ||
      user.role_specialty?.role_group_name === role
    );
  }

  static hasHierarchyLevel(maxLevel: number): boolean {
    const user = StorageService.getUser();
    return user ? user.hierarchy_level <= maxLevel : false;
  }

  static canSupervise(): boolean {
    const user = StorageService.getUser();
    return user?.can_supervise || false;
  }

  static getRoleGroup(): string | null {
    const user = StorageService.getUser();
    return user?.role_specialty?.role_group_name || null;
  }

  static getRoleSpecialty(): string | null {
    const user = StorageService.getUser();
    return user?.role_specialty?.name || null;
  }

  static async refreshTokens(): Promise<boolean> {
    return tokenRefreshService.manualRefresh();
  }
}
